import type { ReactElement } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

// The floor a caller asks for via `contentMinHeight`. Holding it constant stops the popover resizing
// while filtering, which otherwise makes floating-ui flip it between top/bottom — worth it where the
// tree always overflows anyway, wasted whitespace where it does not.
//
// `ComboboxContent` spreads unknown props onto the list box's DOM node, so the transient prop has to be
// filtered here. The cast restores Combobox.Content's generic call signature, which `styled()` erases;
// without it `items`/`renderItem` degrade to `object`.
export const StyledHeightComboboxContent = styled(Combobox.Content).withConfig({
    shouldForwardProp: (prop) => !prop.startsWith("$"),
})<{ $minHeight: number }>`
    min-height: ${({ $minHeight }) => $minHeight}px;
` as <D extends object>(props: Parameters<typeof Combobox.Content<D>>[0] & { $minHeight: number }) => ReactElement;

// The `Combobox.Trigger` target. It stays inside this component rather than being handed to callers:
// `PopoverTrigger` is `asChild`, so it clones its direct child with the floating-ui ref and reference
// props, and a child that does not forward them leaves the trigger inert. A caller's `trigger` node
// renders as a descendant, where its own handlers still run first and can stop the toggle.
export const StyledTriggerSlot = styled.div`
    width: 100%;
    cursor: pointer;
`;

// Virtualized rows sit on a fixed 44px pitch, so a label that wraps to a second line overlaps the row
// below. Every row must stay on one line.
export const StyledTruncatedLabel = styled(Flex)`
    min-width: 0;

    > * {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

export const StyledChevronButton = styled(Flex)`
    flex-shrink: 0;
    padding: 4px;
    cursor: pointer;
    border: none;
    background: none;
`;

export const StyledChevronSpacer = styled.span`
    flex-shrink: 0;
    width: 24px;
`;

export const StyledCount = styled.span`
    flex-shrink: 0;
    color: ${colorsPalettes.carbon[300]};
`;
